import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, BladApi } from '../api/client';
import type { Lista, PodgladImportu, Przedmiot, Zajecia as ZajeciaTyp } from '../api/typy';
import { useAuth } from '../auth/AuthContext';
import {
  Karta, Komunikat, Ladowanie, Plakietka, Pole, Przycisk, Pusto, Zakladki,
} from '../components/ui';
import { PrzyciskExcel } from '../components/PrzyciskExcel';
import { data as fData, dataGodzina, godzina } from '../lib/format';

export function Zajecia() {
  const { maUprawnienie } = useAuth();
  const mozeEdytowac = maUprawnienie('zajecia.zapis');
  const [zakladka, setZakladka] = useState<'lista' | 'import'>('lista');

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-xl font-semibold text-kamien-900">Zajęcia</h1>
        <p className="mt-1 text-sm text-kamien-500">
          Odbiorca wydania w CSM to zajęcia, nie firma. Stąd biorą się raporty zużycia
          w rozbiciu na przedmiot i prowadzącego.
        </p>
      </div>

      {mozeEdytowac ? (
        <Zakladki
          wartosc={zakladka} zmien={setZakladka}
          opcje={[
            { wartosc: 'lista', etykieta: 'Plan zajęć' },
            { wartosc: 'import', etykieta: 'Import z Excela' },
          ]}
        />
      ) : null}

      {zakladka === 'lista' ? <ListaZajec mozeEdytowac={mozeEdytowac} /> : <ImportPlanu />}
    </div>
  );
}

function ListaZajec({ mozeEdytowac }: { mozeEdytowac: boolean }) {
  const [szukaj, setSzukaj] = useState('');
  const [odDzis, setOdDzis] = useState(true);

  const parametry = new URLSearchParams({
    ...(szukaj ? { szukaj } : {}),
    ...(odDzis ? { od: new Date(new Date().setHours(0, 0, 0, 0)).toISOString(), rosnaco: 'true' } : {}),
    naStronie: '100',
  });

  const lista = useQuery({
    queryKey: ['zajecia', parametry.toString()],
    queryFn: () => api.get<Lista<ZajeciaTyp>>(`/zajecia?${parametry}`),
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-64 flex-1">
          <Pole etykieta="Szukaj" value={szukaj} onChange={(e) => setSzukaj(e.target.value)}
            placeholder="Przedmiot, prowadzący albo grupa" />
        </div>
        <label className="flex items-center gap-2 pb-3 text-sm text-kamien-700">
          <input type="checkbox" className="size-4 accent-bordo-600"
            checked={odDzis} onChange={(e) => setOdDzis(e.target.checked)} />
          tylko nadchodzące
        </label>
        <PrzyciskExcel dzial="zajecia" parametry={parametry} className="ml-auto" />
      </div>

      {lista.isLoading ? <Ladowanie /> : (
        <Karta>
          {lista.data && lista.data.pozycje.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[940px] text-sm">
                <thead className="border-b border-kamien-200 text-left text-xs uppercase tracking-wide text-kamien-500">
                  <tr>
                    <th className="px-5 py-3 font-medium">Termin</th>
                    <th className="px-5 py-3 font-medium">Przedmiot</th>
                    <th className="px-5 py-3 font-medium">Grupa</th>
                    <th className="px-5 py-3 font-medium">Prowadzący</th>
                    <th className="px-5 py-3 font-medium">Sala</th>
                    <th className="px-5 py-3 text-right font-medium">Stanowisk</th>
                    <th className="px-5 py-3 text-right font-medium">Studentów</th>
                    <th className="px-5 py-3 font-medium">Powiązania</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-kamien-200">
                  {lista.data.pozycje.map((z) => (
                    <tr key={z.id}>
                      <td className="whitespace-nowrap px-5 py-3">
                        <span className="text-kamien-900">{fData(z.startsAt)}</span>
                        <span className="ml-2 text-kamien-500">
                          {godzina(z.startsAt)}–{godzina(z.endsAt)}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <span className="font-mono text-xs text-kamien-500">{z.course.code}</span>{' '}
                        <span className="text-kamien-900">{z.course.name}</span>
                        {z.course.field && (
                          <span className="block text-xs text-kamien-400">{z.course.field}</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-kamien-700">{z.groupName ?? '—'}</td>
                      <td className="px-5 py-3">
                        <span className="text-kamien-800">{z.instructorName}</span>
                        {z.instructor && (
                          <span className="block text-xs text-kamien-400">konto: {z.instructor.login}</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-kamien-600">{z.room?.code ?? '—'}</td>
                      <td className="px-5 py-3 text-right text-kamien-600">{z.stationCount ?? '—'}</td>
                      <td className="px-5 py-3 text-right text-kamien-600">{z.studentCount ?? '—'}</td>
                      <td className="px-5 py-3">
                        <div className="flex flex-wrap gap-1.5">
                          {z._count.documents > 0 && <Plakietka ton="ok">{z._count.documents} dok.</Plakietka>}
                          {z._count.reservations > 0 && <Plakietka>{z._count.reservations} rezerw.</Plakietka>}
                          {z.importSource && <Plakietka>z planu</Plakietka>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="border-t border-kamien-200 px-5 py-3 text-sm text-kamien-500">
                {lista.data.razem} zajęć
              </p>
            </div>
          ) : (
            <Pusto
              tytul="Brak zajęć"
              opis={mozeEdytowac
                ? 'Zaimportuj plan z Excela — wzorzec pliku leży w docs/szablon-plan-zajec.xlsx.'
                : 'Plan zajęć nie został jeszcze wczytany.'}
            />
          )}
        </Karta>
      )}
    </div>
  );
}

/**
 * Import planu: najpierw podgląd, potem świadomy zapis. Bez tego pierwsza
 * literówka w kodzie przedmiotu założyłaby drugi przedmiot i nikt by tego nie
 * zauważył do końca semestru.
 */
function ImportPlanu() {
  const qc = useQueryClient();
  const wejscie = useRef<HTMLInputElement>(null);
  const [plik, setPlik] = useState<File | null>(null);
  const [podglad, setPodglad] = useState<PodgladImportu | null>(null);
  const [zakladajPrzedmioty, setZakladaj] = useState(false);
  // Domyślnie rozstrzyga sala, nie kierunek (ustalenie z 03.09.2026, wariant C).
  const [kierunki, setKierunki] = useState('');
  const [tylkoZnaneSale, setTylkoZnaneSale] = useState(true);
  const [blad, setBlad] = useState<string | null>(null);
  const [wynik, setWynik] = useState<PodgladImportu['zapisano'] | null>(null);

  const przedmioty = useQuery({
    queryKey: ['przedmioty'], queryFn: () => api.get<{ pozycje: Przedmiot[] }>('/przedmioty'),
  });

  async function wyslij(sciezka: string, zZgoda: boolean) {
    if (!plik) return null;
    const dane = new FormData();
    dane.append('plik', plik);
    if (zZgoda) dane.append('zakladajPrzedmioty', 'true');
    if (kierunki.trim()) dane.append('kierunki', kierunki.trim());
    dane.append('tylkoZnaneSale', tylkoZnaneSale ? 'true' : 'false');

    const odp = await fetch(`/api${sciezka}`, { method: 'POST', credentials: 'include', body: dane });
    const cialo = await odp.json();
    if (!odp.ok) {
      throw new BladApi(cialo.kod ?? 'BLAD_SERWERA', cialo.komunikat ?? 'Import się nie powiódł.', odp.status, cialo.szczegoly);
    }
    return cialo as PodgladImportu;
  }

  const sprawdz = useMutation({
    mutationFn: () => wyslij('/zajecia/import/podglad', false),
    onSuccess: (d) => { setPodglad(d); setWynik(null); setBlad(null); },
    onError: (e) => {
      setPodglad(null);
      setBlad(e instanceof BladApi ? (e.bledyPol.plik ?? e.message) : 'Nie udało się odczytać pliku.');
    },
  });

  const zapisz = useMutation({
    mutationFn: () => wyslij('/zajecia/import/zapis', zakladajPrzedmioty),
    onSuccess: (d) => {
      setWynik(d?.zapisano ?? null);
      setPodglad(null); setPlik(null); setBlad(null);
      if (wejscie.current) wejscie.current.value = '';
      void qc.invalidateQueries({ queryKey: ['zajecia'] });
      void qc.invalidateQueries({ queryKey: ['przedmioty'] });
    },
    onError: (e) => setBlad(e instanceof BladApi ? (e.bledyPol.plik ?? e.message) : 'Zapis się nie powiódł.'),
  });

  const p = podglad?.podsumowanie;

  return (
    <div className="flex flex-col gap-4">
      <Komunikat rodzaj="info" tytul="Jak to działa">
        Plan przygotowuje się w Excelu według wzorca <span className="font-mono">docs/szablon-plan-zajec.xlsx</span>.
        Kolejność kolumn może być dowolna — liczą się nagłówki. Zajęcia rozpoznajemy po kolumnie
        „ID zajęć”, więc powtórny import tego samego pliku zaktualizuje wpisy zamiast je dublować.
      </Komunikat>

      <Karta tytul="Wczytaj plik">
        <div className="flex flex-col gap-4 px-5 py-5">
          <input
            ref={wejscie}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(e) => {
              setPlik(e.target.files?.[0] ?? null);
              setPodglad(null); setWynik(null); setBlad(null);
            }}
            className="text-sm text-kamien-700 file:mr-3 file:min-h-10 file:rounded-md file:border-0
              file:bg-bordo-600 file:px-4 file:text-sm file:font-medium file:text-white
              hover:file:bg-bordo-700"
          />
          <div className="grid gap-4 border-t border-kamien-200 pt-4 sm:grid-cols-2">
            <Pole
              etykieta="Kierunki do wczytania"
              value={kierunki}
              onChange={(e) => setKierunki(e.target.value)}
              podpowiedz="Zwykle zostaw puste — o tym, co wejdzie, rozstrzyga sala. Filtr kierunków to zawężenie dodatkowe."
            />
            <label className="flex cursor-pointer items-start gap-2.5 pt-7">
              <input type="checkbox" className="mt-0.5 size-4 shrink-0 accent-bordo-600"
                checked={tylkoZnaneSale} onChange={(e) => setTylkoZnaneSale(e.target.checked)} />
              <span>
                <span className="block text-sm font-medium text-kamien-800">
                  Tylko sale wpisane do systemu
                </span>
                <span className="block text-xs text-kamien-500">
                  Wczyta wyłącznie zajęcia odbywające się w lokalizacjach oznaczonych jako sale
                  zajęciowe. Dopisanie sali do lokalizacji automatycznie włącza jej zajęcia.
                </span>
              </span>
            </label>
          </div>

          <div>
            <Przycisk wariant="glowny" disabled={!plik || sprawdz.isPending}
              onClick={() => sprawdz.mutate()}>
              {sprawdz.isPending ? 'Sprawdzanie...' : 'Sprawdź plik'}
            </Przycisk>
          </div>
        </div>
      </Karta>

      {blad && <Komunikat rodzaj="blad">{blad}</Komunikat>}

      {wynik && (
        <Komunikat rodzaj="sukces" tytul="Plan zaimportowany">
          Dodano {wynik.nowych}, zaktualizowano {wynik.zaktualizowanych}
          {wynik.zalozonychPrzedmiotow > 0 && `, założono ${wynik.zalozonychPrzedmiotow} nowych przedmiotów`}.
        </Komunikat>
      )}

      {podglad?.format === 'uczelnia' && podglad.adapter && (
        <Komunikat rodzaj="info" tytul="Rozpoznano eksport z planu uczelni">
          <p>
            Plik obejmuje {podglad.adapter.zakresDat
              ? `dni ${podglad.adapter.zakresDat.od} – ${podglad.adapter.zakresDat.do}`
              : 'zakres podany w wierszach tytułowych'}.
            Rok bierzemy z nagłówka bloku, bo kolumna z datą podaje wyłącznie dzień i miesiąc.
          </p>
          <table className="mt-3 text-sm">
            <tbody>
              {podglad.adapter.kierunkiWPliku.map((k) => (
                <tr key={k.kod}>
                  <td className="pr-3 font-mono text-xs">{k.kod}</td>
                  <td className="pr-4">{k.nazwa}</td>
                  <td className="pr-3 text-right tabular-nums">{k.zajec}</td>
                  <td>{k.wCsm ? <Plakietka ton="ok">CSM</Plakietka> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {podglad.adapter.pominiete.length > 0 && (
            <p className="mt-3">
              Pominięto {podglad.adapter.pominiete.length} wierszy:{' '}
              {[...new Set(podglad.adapter.pominiete.map((x) => x.powod))].join(', ')}.
            </p>
          )}
        </Komunikat>
      )}

      {podglad && p && (
        <>
          <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-lg bg-kamien-200 ring-1 ring-kamien-200 sm:grid-cols-4">
            <Kafelek etykieta="Wczytanych" wartosc={String(p.wczytanych)} />
            <Kafelek etykieta="Nowych" wartosc={String(p.nowych)} />
            <Kafelek etykieta="Do aktualizacji" wartosc={String(p.doAktualizacji)} />
            <Kafelek etykieta="Z błędami" wartosc={String(p.zBledami)} ton={p.zBledami > 0 ? 'blad' : undefined} />
          </dl>

          {podglad.bledy.length > 0 && (
            <Komunikat rodzaj="blad" tytul="Wiersze, których nie da się wczytać">
              <ul className="mt-1 list-inside list-disc">
                {podglad.bledy.map((b, i) => (
                  <li key={i}>Wiersz {b.nrWiersza}, kolumna „{b.pole}”: {b.komunikat}</li>
                ))}
              </ul>
              <p className="mt-2">Pozostałe wiersze zostaną zaimportowane normalnie.</p>
            </Komunikat>
          )}

          {p.nieznanePrzedmioty.length > 0 && (
            <Komunikat rodzaj="uwaga" tytul="Przedmioty spoza systemu">
              <ul className="mt-1 list-inside list-disc">
                {p.nieznanePrzedmioty.map((n) => <li key={n.kod}><span className="font-mono">{n.kod}</span> — {n.nazwa}</li>)}
              </ul>
              <label className="mt-3 flex cursor-pointer items-start gap-2">
                <input type="checkbox" className="mt-0.5 size-4 accent-bordo-600"
                  checked={zakladajPrzedmioty} onChange={(e) => setZakladaj(e.target.checked)} />
                <span>
                  Załóż te przedmioty automatycznie.
                  <span className="block text-xs">
                    Sprawdź kody — literówka utworzy drugi przedmiot obok właściwego,
                    a raport zużycia rozpadnie się na dwa.
                  </span>
                </span>
              </label>
            </Komunikat>
          )}

          {(p.nieznaniProwadzacy.length > 0 || p.nieznaneSale.length > 0) && (
            <Komunikat rodzaj="uwaga" tytul="Do sprawdzenia, ale import przejdzie">
              {p.nieznaniProwadzacy.length > 0 && (
                <p>Brak kont o loginach: {p.nieznaniProwadzacy.join(', ')} — zostanie samo nazwisko.</p>
              )}
              {p.nieznaneSale.length > 0 && (
                <p>Brak lokalizacji o kodach: {p.nieznaneSale.join(', ')} — zajęcia zapiszą się bez sali.</p>
              )}
            </Komunikat>
          )}

          <Karta tytul="Co zostanie zapisane">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[880px] text-sm">
                <thead className="border-b border-kamien-200 text-left text-xs uppercase tracking-wide text-kamien-500">
                  <tr>
                    <th className="px-5 py-3 font-medium">Wiersz</th>
                    <th className="px-5 py-3 font-medium">Termin</th>
                    <th className="px-5 py-3 font-medium">Przedmiot</th>
                    <th className="px-5 py-3 font-medium">Grupa</th>
                    <th className="px-5 py-3 font-medium">Prowadzący</th>
                    <th className="px-5 py-3 font-medium">Sala</th>
                    <th className="px-5 py-3 font-medium">Stan</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-kamien-200">
                  {podglad.wiersze.map((w) => (
                    <tr key={w.nrWiersza} title={w.uwagi.join('\n')}>
                      <td className="px-5 py-2.5 text-kamien-400">{w.nrWiersza}</td>
                      <td className="whitespace-nowrap px-5 py-2.5 text-kamien-700">
                        {dataGodzina(w.startsAt)}–{godzina(w.endsAt)}
                      </td>
                      <td className="px-5 py-2.5">
                        <span className={`font-mono text-xs ${w.przedmiotZnany ? 'text-kamien-500' : 'text-status-uzupelnij'}`}>
                          {w.kodPrzedmiotu}
                        </span>{' '}
                        <span className="text-kamien-800">{w.przedmiot}</span>
                      </td>
                      <td className="px-5 py-2.5 text-kamien-600">{w.grupa ?? '—'}</td>
                      <td className={`px-5 py-2.5 ${w.prowadzacyZnany ? 'text-kamien-800' : 'text-kamien-500'}`}>
                        {w.prowadzacy}
                      </td>
                      <td className={`px-5 py-2.5 ${w.salaZnana ? 'text-kamien-600' : 'text-status-uzupelnij'}`}>
                        {w.sala ?? '—'}
                      </td>
                      <td className="px-5 py-2.5">
                        <Plakietka ton={w.stan === 'NOWE' ? 'ok' : 'neutralny'}>
                          {w.stan === 'NOWE' ? 'nowe' : 'aktualizacja'}
                        </Plakietka>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex flex-wrap items-center gap-3 border-t border-kamien-200 px-5 py-4">
              <Przycisk
                wariant="glowny"
                disabled={zapisz.isPending || (p.nieznanePrzedmioty.length > 0 && !zakladajPrzedmioty)}
                onClick={() => zapisz.mutate()}
              >
                {zapisz.isPending ? 'Zapisywanie...' : `Zapisz ${p.wczytanych} zajęć`}
              </Przycisk>
              <Przycisk wariant="cichy" onClick={() => setPodglad(null)}>Anuluj</Przycisk>
              {p.nieznanePrzedmioty.length > 0 && !zakladajPrzedmioty && (
                <span className="text-sm text-status-uzupelnij">
                  Najpierw zdecyduj, co z nieznanymi przedmiotami.
                </span>
              )}
            </div>
          </Karta>
        </>
      )}

      {przedmioty.data && przedmioty.data.pozycje.length > 0 && (
        <Karta tytul="Przedmioty w systemie" opis="Kody, po których import rozpoznaje zajęcia">
          <ul className="divide-y divide-kamien-200">
            {przedmioty.data.pozycje.map((p2) => (
              <li key={p2.id} className="flex items-center gap-3 px-5 py-2.5 text-sm">
                <span className="font-mono text-xs text-kamien-500">{p2.code}</span>
                <span className="flex-1 text-kamien-800">{p2.name}</span>
                {p2.field && <span className="text-xs text-kamien-400">{p2.field}</span>}
                <Plakietka>{p2._count.classes} zajęć</Plakietka>
              </li>
            ))}
          </ul>
        </Karta>
      )}

      <p className="text-sm text-kamien-500">
        Nie masz wzorca? Leży w repozytorium: <span className="font-mono">docs/szablon-plan-zajec.xlsx</span>.
        Zawiera arkusz z nagłówkami, trzy wiersze przykładowe i opis każdej kolumny.{' '}
        <Link to="/zestawy" className="underline underline-offset-2">Przejdź do zestawów</Link>, żeby
        sprawdzić kompletność wyposażenia na zaimportowane zajęcia.
      </p>
    </div>
  );
}

function Kafelek({ etykieta, wartosc, ton }: { etykieta: string; wartosc: string; ton?: 'blad' }) {
  return (
    <div className="bg-white px-4 py-3">
      <dt className="text-[11px] uppercase tracking-wide text-kamien-500">{etykieta}</dt>
      <dd className={`mt-0.5 text-lg font-semibold ${ton === 'blad' ? 'text-status-brak' : 'text-kamien-900'}`}>
        {wartosc}
      </dd>
    </div>
  );
}
